import React, { useEffect, useRef, useState } from 'react';
import '../styles/SynchronizationExample.css';

const IMPLICIT_DELAY_MS = 3000;
const EXPLICIT_DELAY_MS = 4000;
const FLUENT_LOADING_DELAY_MS = 1500;
const FLUENT_COMPLETE_DELAY_MS = 4500;

const SynchronizationExample = () => {
  const [implicitState, setImplicitState] = useState('idle');
  const [explicitState, setExplicitState] = useState('idle');
  const [fluentState, setFluentState] = useState('idle');
  const [fluentStatusText, setFluentStatusText] = useState('');

  const implicitTimerRef = useRef(null);
  const explicitTimerRef = useRef(null);
  const fluentTimersRef = useRef([]);

  const clearFluentTimers = () => {
    fluentTimersRef.current.forEach(clearTimeout);
    fluentTimersRef.current = [];
  };

  const scheduleFluentTimer = (callback, delay) => {
    const timerId = setTimeout(callback, delay);
    fluentTimersRef.current.push(timerId);
  };

  const startImplicitDemo = () => {
    if (implicitTimerRef.current) {
      clearTimeout(implicitTimerRef.current);
    }
    setImplicitState('loading');
    implicitTimerRef.current = setTimeout(() => {
      setImplicitState('ready');
    }, IMPLICIT_DELAY_MS);
  };

  const startExplicitDemo = () => {
    if (explicitTimerRef.current) {
      clearTimeout(explicitTimerRef.current);
    }
    setExplicitState('loading');
    explicitTimerRef.current = setTimeout(() => {
      setExplicitState('ready');
    }, EXPLICIT_DELAY_MS);
  };

  const startFluentDemo = () => {
    clearFluentTimers();
    setFluentState('loading');
    setFluentStatusText('Payment status: pending');

    scheduleFluentTimer(() => {
      setFluentStatusText('Payment status: processing');
    }, FLUENT_LOADING_DELAY_MS);

    scheduleFluentTimer(() => {
      setFluentStatusText('Payment status: complete');
      setFluentState('ready');
    }, FLUENT_COMPLETE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (implicitTimerRef.current) {
        clearTimeout(implicitTimerRef.current);
      }
      if (explicitTimerRef.current) {
        clearTimeout(explicitTimerRef.current);
      }
      clearFluentTimers();
    };
  }, []);

  return (
    <div className="sync-page" id="synchronization-page">
      <section className="sync-section" id="implicit-wait-section">
        <div className="sync-card">
          <h3 className="sync-section-title">Implicit Wait</h3>
          <p className="sync-behavior">
            Click Load User Profile to fetch profile details. The profile appears after a
            short delay, similar to content loading on a live dashboard.
          </p>

          <div className="sync-action-row">
            <button
              type="button"
              className="sync-button sync-button--secondary"
              id="load-user-profile-button"
              onClick={startImplicitDemo}
              disabled={implicitState === 'loading'}
            >
              {implicitState === 'loading' ? 'Loading profile...' : 'Load User Profile'}
            </button>

            <div className="sync-demo-area" id="implicit-wait-demo">
              {implicitState === 'loading' && (
                <p className="sync-loading" id="implicit-wait-loading">
                  Loading profile...
                </p>
              )}
              {implicitState === 'ready' && (
                <p className="sync-result" id="implicit-wait-result" role="status">
                  Profile loaded successfully.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="sync-section" id="explicit-wait-section">
        <div className="sync-card">
          <h3 className="sync-section-title">Explicit Wait</h3>
          <p className="sync-behavior">
            Click Place Order to submit an order. A confirmation message appears only after
            the server finishes processing, like a real checkout flow.
          </p>

          <div className="sync-action-row">
            <button
              type="button"
              className="sync-button"
              id="place-order-button"
              onClick={startExplicitDemo}
              disabled={explicitState === 'loading'}
            >
              {explicitState === 'loading' ? 'Placing order...' : 'Place Order'}
            </button>

            <div className="sync-demo-area" id="explicit-wait-demo">
              {explicitState === 'loading' && (
                <p className="sync-loading" id="explicit-wait-loading">
                  Processing your order...
                </p>
              )}
              {explicitState === 'ready' && (
                <p className="sync-result" id="explicit-wait-result" role="status">
                  Order placed successfully! Your order confirmation is now visible.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="sync-section" id="fluent-wait-section">
        <div className="sync-card">
          <h3 className="sync-section-title">Fluent Wait</h3>
          <p className="sync-behavior">
            Click Track Payment Status to monitor a payment. The status updates in stages,
            like polling a payment gateway until the transaction completes.
          </p>

          <div className="sync-action-row">
            <button
              type="button"
              className="sync-button"
              id="track-payment-status-button"
              onClick={startFluentDemo}
              disabled={fluentState === 'loading'}
            >
              {fluentState === 'loading' ? 'Tracking payment...' : 'Track Payment Status'}
            </button>

            <div className="sync-demo-area" id="fluent-wait-demo">
              {fluentState !== 'idle' && (
                <p
                  className={`sync-status ${fluentState === 'ready' ? 'sync-status--complete' : ''}`}
                  id="fluent-wait-status"
                  role="status"
                >
                  {fluentStatusText}
                </p>
              )}
              {fluentState === 'ready' && (
                <p className="sync-result" id="fluent-wait-result" role="status">
                  Payment completed successfully! Funds have been received.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SynchronizationExample;
